import { Injectable, signal, Signal } from "@angular/core";
import {
  AppEvent,
  AppEventId,
  AuthResultEvent,
  LocalStorageEvent,
} from "@loader-event-bus/app-event.model";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import { EventBusClient } from "./events/event-bus.client";
import { ParentLocationUrlValue } from "./parent-location-url.value";
import { DeviceUtil } from "./util";

export type DeviceType = "mobile" | "wide";

/**
 * A service used to store data related to the device and the browser tab.
 */
@Injectable({ providedIn: "root" })
export class AppHostService {
  private readonly _isSmallScreen = signal(false);
  private readonly _isTestBot = signal(false);
  private readonly _stage$ = new BehaviorSubject<string | null>(null);
  private readonly _isLoaderReady$ = new BehaviorSubject<boolean>(false);
  private readonly _dragEnd$ = new Subject<void>();
  private readonly _authResult$ = new Subject<AuthResultEvent["data"]>();
  private readonly _openLinkFailed$ = new Subject<string>();

  public readonly focusOnUserInput$ = new Subject<number | null>();
  public readonly dragEnd$ = this._dragEnd$.asObservable();
  /** Emits the URL of each link the host could not open (e.g. popup blocked). */
  public readonly openLinkFailed$: Observable<string> = this._openLinkFailed$.asObservable();
  public readonly authResult$: Observable<AuthResultEvent["data"]> =
    this._authResult$.asObservable();

  private readonly _channelReady$ = new ReplaySubject<void>(1);
  public readonly channelReady$: Observable<void> = this._channelReady$.asObservable();

  private readonly eventSubjects = new Map<string, Subject<unknown>>();

  constructor(
    private readonly parentLocationUrl: ParentLocationUrlValue,
    private readonly device: DeviceUtil,
    private readonly eventBus: EventBusClient
  ) {
    this.eventBus
      .waitForInit((message) => this.onEvent(message))
      .then(() => this._channelReady$.next());
  }

  private onEvent(event: AppEvent): void {
    switch (event.eventId) {
      case AppEventId.LOADER_READY:
        this._isLoaderReady$.next(true);
        break;
      case AppEventId.URL:
        this.parentLocationUrl.set(event.data);
        break;
      case AppEventId.IS_TEST_BOT:
        this._isTestBot.set(!!event.data);
        break;
      case AppEventId.IS_SMALL_SCREEN:
        this._isSmallScreen.set(event.data);
        break;
      case AppEventId.FOCUS_AFTER_IMAGE_CLOSED:
        this.focusOnUserInput$.next(Date.now());
        break;
      case AppEventId.STAGE:
        this._stage$.next(event.data);
        break;
      case AppEventId.DRAG_END:
        this._dragEnd$.next();
        break;
      case AppEventId.AUTH_RESULT:
        this._authResult$.next(event.data);
        break;
      case AppEventId.OPEN_LINK_FAILED:
        this._openLinkFailed$.next(event.data);
        break;
      default: {
        // Local-storage response events (e.g. "conversationId", "open") arrive here at
        // runtime. They are NOT in the AppEvent union and are handled dynamically.
        const localStorageEvent = event as unknown as LocalStorageEvent<unknown>;
        if (this.eventSubjects.has(localStorageEvent.eventId)) {
          this.eventSubjects.get(localStorageEvent.eventId)?.next(localStorageEvent.data);
        }
        break;
      }
    }
  }

  /** @return The current browser tab URL */
  get currentUrl(): string {
    return this.parentLocationUrl.value;
  }

  /** @return Whether the application is being executed on a small screen */
  get isSmallScreen(): Signal<boolean> {
    return this._isSmallScreen.asReadonly();
  }

  /** @return The device type on which the application is running (mobile or wide) */
  get deviceType(): DeviceType {
    return this.isMobile ? "mobile" : "wide";
  }

  /** @return Whether the application is being executed on mobile */
  get isMobile(): boolean {
    return this.device.isMobile;
  }

  /** @return Whether the current bot is a test one */
  get isTestBot(): Signal<boolean> {
    return this._isTestBot.asReadonly();
  }

  /** @return Whether the host is ready for listening to events */
  get isLoaderReady$(): Observable<boolean> {
    return this._isLoaderReady$.asObservable();
  }

  /** @return The current loader stage */
  get stage$(): Observable<string | null> {
    return this._stage$.asObservable();
  }

  /**
   * Sends a typed event to the host.
   * @param event - The AppEvent to send.
   */
  public send(event: AppEvent): void {
    this.eventBus.sendEventMessage(event);
  }

  /**
   * Sends a dynamic local-storage event to the host.
   * @param eventId - The event ID (e.g., 'get_conversationId', 'set_open').
   * @param data - The optional payload.
   */
  public sendLocalStorageEvent(eventId: string, data: unknown = null): void {
    this.eventBus.sendEventMessage({ eventId, data });
  }

  register(eventId: string, subject: Subject<unknown>): void {
    this.eventSubjects.set(eventId, subject);
  }
}
